import React from 'react';
import { Icon, InlineMessage } from '@saviynt/design-system';

import { msgs } from '../../utilities/messages';

/* ******************************
 * ******* ALERT BANNERS ********
 * **************************** */

/**
 * Banner shown after a key is successfully deleted.
 * @param {Function} localize - the localize function from useLocalize
 * @returns {Object} an alert-banner descriptor
 */
export const deleteSuccessBanner = (localize) => ({
  colorTheme: 'Information',
  title: localize(msgs.pam.extend.alertBannerSuccessTitle),
  description: localize(msgs.pam.apiKeys.deleteSuccessDescription),
});

/**
 * Banner shown when a key deletion fails.
 * @param {Function} localize - the localize function from useLocalize
 * @returns {Object} an alert-banner descriptor
 */
export const deleteFailureBanner = (localize) => ({
  colorTheme: 'Critical',
  title: localize(msgs.pam.extend.alertBannerFailureTitle),
  description: localize(msgs.pam.apiKeys.deleteFailureDescription),
});

/**
 * Banner shown after a key is successfully generated and downloaded.
 * @param {Function} localize - the localize function from useLocalize
 * @returns {Object} an alert-banner descriptor
 */
export const generateSuccessBanner = (localize) => ({
  colorTheme: 'Success',
  title: localize(msgs.pam.extend.alertBannerSuccessTitle),
  description: localize(msgs.pam.apiKeys.generateSuccessDescription),
});

/**
 * Banner shown when key generation fails.
 * @param {Function} localize - the localize function from useLocalize
 * @returns {Object} an alert-banner descriptor
 */
export const generateFailureBanner = (localize) => ({
  colorTheme: 'Critical',
  title: localize(msgs.pam.extend.alertBannerFailureTitle),
  description: localize(msgs.pam.apiKeys.generateFailureDescription),
});

/* ******************************
 * ******* KEY CONFIG ***********
 * **************************** */

/**
 * Normalizes the string/number key-policy props passed from the landing page into
 * the numeric/boolean shape the DownloadKeysModal works with.
 * @param {Object} props - the raw ssh key policy props
 * @returns {Object} parsed { keyValidityHours, isPassphraseMandatory, minPassphraseLength, maxPassphraseLength }
 */
export const parseKeyConfig = ({
  sshKeyExpiryDurationInHours,
  sshKeyPassphraseMandatory,
  sshKeyPassphraseMinLength,
  sshKeyPassphraseMaxLength,
}) => ({
  keyValidityHours: Number(sshKeyExpiryDurationInHours),
  isPassphraseMandatory:
    sshKeyPassphraseMandatory === 'true' || sshKeyPassphraseMandatory === '1',
  minPassphraseLength: Number(sshKeyPassphraseMinLength),
  maxPassphraseLength: Number(sshKeyPassphraseMaxLength),
});

/* ******************************
 * **** CRITICAL HELPER TEXT ****
 * **************************** */

/**
 * A small critical InlineMessage used for inline field errors.
 * @param {string} text - the already-localized error text
 * @returns {Element} an InlineMessage element
 */
export const criticalMessage = (text) => (
  <InlineMessage
    text={text}
    colorTheme='critical'
    size='small'
    leftIcon={<Icon kind='AlertCriticalFilled' />}
  />
);

/**
 * Resolves the critical helper text for the passphrase field.
 * @returns {Element|null} the InlineMessage to show, or null when the field is valid
 */
export const getPassphraseCriticalText = (
  localize,
  isSubmitted,
  isPassphraseMandatory,
  passphrase,
  passphraseLengthInvalid,
  minLen,
  maxLen
) => {
  if (!isSubmitted) return null;

  if (isPassphraseMandatory && passphrase.length === 0) {
    return <InlineMessage kind='missingRequiredField' size='small' />;
  }

  if (passphraseLengthInvalid) {
    return criticalMessage(
      localize(msgs.pam.apiKeys.download.passphraseLengthError, {
        min: minLen,
        max: maxLen,
      })
    );
  }

  return null;
};

/**
 * Resolves the critical helper text for the confirm-passphrase field.
 * @returns {Element|null} the InlineMessage to show, or null when the field is valid
 */
export const getConfirmCriticalText = (
  localize,
  isSubmitted,
  confirmPassphrase,
  passphrasesMismatch
) => {
  if (!isSubmitted) return null;

  if (confirmPassphrase.length === 0) {
    return <InlineMessage kind='missingRequiredField' size='small' />;
  }

  if (passphrasesMismatch) {
    return criticalMessage(
      localize(msgs.pam.apiKeys.download.passphraseMismatch)
    );
  }

  return null;
};
